// Certificate Generator Utility
// Generates professional compliance and recognition certificates

export interface SafetyInitiative {
  id: string;
  title: string;
  subtitle: string;
  recognitionText: string;
  themeColor: string;
  emoji: string;
}

export const SAFETY_INITIATIVES: SafetyInitiative[] = [
  {
    id: "safety-ambassador",
    title: "OnMyWay Safety Ambassador",
    subtitle: "HIGHEST RECOGNITION",
    recognitionText:
      "for maintaining outstanding safety scores over an extended period and inspiring others to adopt the OnMyWay safety platform.",
    themeColor: "#E6B53C",
    emoji: "⭐",
  },
  {
    id: "distracted-driving",
    title: "Distracted Driving Prevention",
    subtitle: "SPECIALIST CERTIFICATION",
    recognitionText:
      "for completing OnMyWay training modules on the dangers of phone usage while driving, validated by consistent in-app safe driving behavior.",
    themeColor: "#3FA9FF",
    emoji: "🚗",
  },
  {
    id: "mobile-workplace",
    title: "Mobile-First Workplace Safety",
    subtitle: "CERTIFICATION",
    recognitionText:
      "for utilizing the OnMyWay app to report workplace hazards and consistently following digital safety protocols.",
    themeColor: "#7CC242",
    emoji: "👷",
  },
  {
    id: "archie-plus",
    title: "Archie+ Personal Security",
    subtitle: "COMPANION / BODYGUARD",
    recognitionText:
      "for proficiency in using OnMyWay Archie+, the AI-powered real-time walking safety companion that protects users on the move.",
    themeColor: "#A974FF",
    emoji: "🤖",
  },
  {
    id: "ai-threat-detector",
    title: "Verified AI Threat Detector",
    subtitle: "CERTIFICATION",
    recognitionText:
      "for demonstrating skill in recognizing and preventing phishing, deepfakes, and digital impersonation through the OnMyWay AI Threat platform.",
    themeColor: "#2BD4C8",
    emoji: "🛡️",
  },
  {
    id: "lone-worker",
    title: "Remote & Lone Worker",
    subtitle: "SAFETY PROTOCOL SPECIALIST",
    recognitionText:
      "for validated compliance with remote work safety using OnMyWay automated check-ins, panic alerts, and lone worker protections.",
    themeColor: "#FF8A3D",
    emoji: "👤",
  },
  {
    id: "ridewatch",
    title: "RideWatch Safety Companion",
    subtitle: "CERTIFIED",
    recognitionText:
      "for active use of OnMyWay RideWatch - covering rideshare trip tracking, live monitoring, and emergency alerts.",
    themeColor: "#3FA9FF",
    emoji: "🚕",
  },
  {
    id: "mental-health-shield",
    title: "Mental Health & Wellness",
    subtitle: "SHIELD ADVOCATE",
    recognitionText:
      "for completing OnMyWay workshops on stress management and consistently using the Mental Health Shield tool.",
    themeColor: "#E5484D",
    emoji: "❤️",
  },
  {
    id: "dol-digital-reporter",
    title: "US Dept. of Labor",
    subtitle: "COMPLIANT DIGITAL REPORTER",
    recognitionText:
      "for demonstrating proper reporting protocols within the OnMyWay Workplace Emergency Program in alignment with DOL guidance.",
    themeColor: "#A974FF",
    emoji: "📄",
  },
  {
    id: "workplace-emergency",
    title: "Workplace Emergency",
    subtitle: "PROTOCOLS / PLAN",
    recognitionText:
      "awarded for ranking in the top 5% of safe drivers within the company on the OnMyWay platform and mastering emergency protocols.",
    themeColor: "#7CC242",
    emoji: "!",
  },
];

export interface CertificateData {
  employeeName: string;
  companyName: string;
  initiative: SafetyInitiative;
}

// Draw rounded rectangle
const roundRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number
) => {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
};

// Wrap text to fit within width
const wrapText = (
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number
): number => {
  const words = text.split(' ');
  let line = '';
  let currentY = y;

  for (let n = 0; n < words.length; n++) {
    const testLine = line + words[n] + ' ';
    const metrics = ctx.measureText(testLine);
    const testWidth = metrics.width;
    
    if (testWidth > maxWidth && n > 0) {
      ctx.fillText(line, x, currentY);
      line = words[n] + ' ';
      currentY += lineHeight;
    } else {
      line = testLine;
    }
  }
  ctx.fillText(line, x, currentY);
  return currentY;
};

// Generate professional compliance certificate matching HTML design
let sidebarLogoCache: HTMLImageElement | null = null;
let sidebarLogoPromise: Promise<HTMLImageElement> | null = null;

const loadSidebarLogo = (): Promise<HTMLImageElement> => {
  if (sidebarLogoCache) return Promise.resolve(sidebarLogoCache);
  if (sidebarLogoPromise) return sidebarLogoPromise;

  sidebarLogoPromise = new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      sidebarLogoCache = img;
      resolve(img);
    };
    img.onerror = () => reject(new Error("Failed to load sidebar logo"));
    img.src = "/logo.png";
  });

  return sidebarLogoPromise;
};

export const generateCertificate = async (
  data: CertificateData,
): Promise<HTMLCanvasElement> => {
  const canvas = document.createElement('canvas');
  canvas.width = 1200;
  canvas.height = 850;
  const ctx = canvas.getContext('2d');
  
  if (!ctx) throw new Error('Could not get canvas context');

  // Background gradient (matching HTML: #0b1020 -> #111827 -> #0f172a)
  const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
  gradient.addColorStop(0, '#0b1020');
  gradient.addColorStop(0.5, '#111827');
  gradient.addColorStop(1, '#0f172a');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, canvas.width, canvas.height);

  // Outer border with theme color
  ctx.strokeStyle = data.initiative.themeColor;
  ctx.lineWidth = 8;
  roundRect(ctx, 0, 0, canvas.width, canvas.height, 18);
  ctx.stroke();

  // Inner border
  ctx.strokeStyle = 'rgba(255,255,255,0.12)';
  ctx.lineWidth = 2;
  roundRect(ctx, 18, 18, canvas.width - 36, canvas.height - 36, 12);
  ctx.stroke();

  // Top bar - Brand section (left)
  const logoX = 70;
  const logoY = 70;
  const logoWidth = 180;
  const logoHeight = 56;

  try {
    const sidebarLogo = await loadSidebarLogo();
    ctx.drawImage(sidebarLogo, logoX, logoY, logoWidth, logoHeight);
  } catch {
    ctx.textAlign = 'left';
    ctx.font = 'bold 38px Arial, sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.fillText('OnMyWay', logoX, logoY + 38);
  }
  
  // Brand text
  ctx.textAlign = 'left';
  ctx.font = 'bold 26px Arial, sans-serif';
  ctx.fillStyle = '#ffffff';
  ctx.fillText(data.companyName || 'OnMyWay', logoX, logoY + logoHeight + 28);
  
  ctx.font = '14px Arial, sans-serif';
  ctx.fillStyle = 'rgba(255,255,255,0.7)';
  ctx.fillText('SAFETY. ANYWHERE. ANYTIME.', logoX, logoY + logoHeight + 50);

  // Top bar - Verified badge (right)
  const badgeX = canvas.width - 200;
  const badgeY = 70;
  const badgeWidth = 130;
  const badgeHeight = 80;
  
  ctx.fillStyle = 'rgba(255,255,255,0.05)';
  roundRect(ctx, badgeX, badgeY, badgeWidth, badgeHeight, 14);
  ctx.fill();
  
  ctx.strokeStyle = 'rgba(255,255,255,0.2)';
  ctx.lineWidth = 1;
  ctx.stroke();
  
  ctx.textAlign = 'center';
  ctx.font = '13px Arial, sans-serif';
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  ctx.fillText('VERIFIED', badgeX + badgeWidth / 2, badgeY + 32);
  ctx.fillText('SAFETY', badgeX + badgeWidth / 2, badgeY + 48);
  ctx.fillText('INITIATIVE', badgeX + badgeWidth / 2, badgeY + 64);

  // Content section
  const contentY = 240;
  
  // Certificate type (title)
  ctx.textAlign = 'center';
  ctx.font = 'bold 54px Arial, sans-serif';
  ctx.fillStyle = '#ffffff';
  ctx.fillText(data.initiative.title.toUpperCase(), canvas.width / 2, contentY);
  
  // Subtitle
  ctx.font = '24px Arial, sans-serif';
  ctx.fillStyle = '#93c5fd';
  ctx.fillText(data.initiative.subtitle, canvas.width / 2, contentY + 40);
  
  // "This certificate recognizes"
  ctx.font = '28px Arial, sans-serif';
  ctx.fillStyle = 'rgba(255,255,255,0.8)';
  ctx.fillText('This certificate recognizes', canvas.width / 2, contentY + 110);
  
  // User name
  ctx.font = 'bold 72px Arial, sans-serif';
  ctx.fillStyle = '#3b82f6';
  ctx.fillText(data.employeeName, canvas.width / 2, contentY + 190);
  
  // Description text (wrapped)
  ctx.font = '26px Arial, sans-serif';
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  const descY = contentY + 260;
  const maxWidth = 850;
  const lineHeight = 47;
  wrapText(ctx, data.initiative.recognitionText, canvas.width / 2, descY, maxWidth, lineHeight);

  // Bottom section - Signature
  const bottomY = canvas.height - 120;
  
  // Signature line
  ctx.strokeStyle = 'rgba(255,255,255,0.5)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(canvas.width / 2 - 130, bottomY);
  ctx.lineTo(canvas.width / 2 + 130, bottomY);
  ctx.stroke();
  
  // Signature name
  ctx.textAlign = 'center';
  ctx.font = '26px cursive';
  ctx.fillStyle = '#ffffff';
  ctx.fillText('OnMyWay Team', canvas.width / 2, bottomY + 30);
  
  // Signature role
  ctx.font = '16px Arial, sans-serif';
  ctx.fillStyle = 'rgba(255,255,255,0.8)';
  ctx.fillText('OnMyWay Safety Initiative', canvas.width / 2, bottomY + 55);

  return canvas;
};

// Convert canvas to blob
export const canvasToBlob = (canvas: HTMLCanvasElement): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error('Failed to convert canvas to blob'));
      }
    }, 'image/png');
  });
};

// Download single certificate
export const downloadCertificate = async (data: CertificateData) => {
  const canvas = await generateCertificate(data);
  const blob = await canvasToBlob(canvas);
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  const safeInitiativeId = data.initiative.id.replace(/\s+/g, '_');
  link.download = `${data.employeeName.replace(/\s+/g, '_')}_${safeInitiativeId}.png`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
